import { FormsyRadioGroup, Radio } from '@thanx/uikit/radio'
import {
  getDeliveryOptimizationSetting,
  GET_DELIVERY_OPTIMIZATION_CONFIG_SUCCESS,
  updateDeliveryOptimizationSetting,
  UPDATE_DELIVERY_OPTIMIZATION_CONFIG_SUCCESS,
} from 'actions/config'
import { alert } from 'actions/flash'
import Badges from 'components/Badges'
import ConfirmModal from 'components/ConfirmModal'
import Section from 'components/Section'
import Formsy from 'formsy-react'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import {
  DeliveryOptimizationConfigApiFields,
  DeliveryOptimizationSetting as SelectionType,
} from 'models/Config'
import React, { useCallback, useEffect, useState } from 'react'
import CmsPage from 'scenes/Cms/components/CmsPage'
import PublishBanner from 'scenes/Cms/components/PublishBanner'
import OrderingNotEnabledPage from '../OrderingNotEnabledPage'
import Sidebar from './Sidebar'

const t = buildTranslate('cms.settings.delivery_default_logic')

type FormData = {
  deliverySelection: SelectionType | undefined
}

const DeliveryDefaultLogic: React.FC<{ showTitle?: boolean }> = ({
  showTitle = true,
}) => {
  const merchant = useCurrentMerchant()

  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [formData, setFormData] = useState<FormData>({
    deliverySelection: undefined,
  })

  const displayAlert = useCallback(
    (key: 'danger' | 'success', messageKey: string) => {
      dispatch(
        alert({
          key,
          message: t(messageKey),
          timeout: 5,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      const response = await dispatch(getDeliveryOptimizationSetting())
      if (response.type === GET_DELIVERY_OPTIMIZATION_CONFIG_SUCCESS) {
        const delivery_optimization: DeliveryOptimizationConfigApiFields =
          response.payload.data.delivery_optimization
        if (delivery_optimization) {
          setFormData({
            deliverySelection: delivery_optimization.setting,
          })
        }
      } else {
        displayAlert('danger', 'fetch_error')
      }
      setIsLoading(false)
    }

    fetchData()
  }, [dispatch, displayAlert])

  const handlePublish = async () => {
    setIsSubmitting(true)

    if (!formData.deliverySelection) {
      displayAlert('danger', 'form_error')
    } else {
      const response = await dispatch(
        updateDeliveryOptimizationSetting(formData.deliverySelection)
      )

      if (response.type === UPDATE_DELIVERY_OPTIMIZATION_CONFIG_SUCCESS) {
        displayAlert('success', 'submit_success')
      } else {
        displayAlert('danger', 'submit_error')
      }
    }

    setIsModalOpen(false)
    setIsSubmitting(false)
  }

  if (!merchant?.ordering_enabled) {
    return <OrderingNotEnabledPage />
  }

  return (
    <Formsy
      onValidSubmit={() => {
        setIsModalOpen(true)
      }}
      onChange={(newFormData: FormData) => {
        setFormData(newFormData)
      }}
      onInvalidSubmit={() =>
        dispatch(
          alert({
            key: 'danger',
            message: t('form_error'),
            timeout: 5,
          })
        )
      }
    >
      <CmsPage
        showTitle={showTitle}
        title={t('title')}
        description={`${t(
          'description'
        )} <a href="https://help.thanx.com/hc/en-us/articles/32762347896855-Delivery-Default-Logic" target="_blank" rel="noopener noreferrer" class="text-decoration-none">${t(
          'learn_more'
        )}</a>`}
        sidebar={<Sidebar isLoading={isSubmitting} disabled={false} />}
        isLoading={isLoading}
      >
        <Section className="p-m mb-m">
          <h4 className="mb-s">{t('web_and_app.title')}</h4>
          <FormsyRadioGroup
            name="deliverySelection"
            value={formData.deliverySelection}
          >
            <Radio value={SelectionType.NONE}>
              {t('web_and_app.allow_customers')}
            </Radio>
            {formData.deliverySelection === SelectionType.NONE && (
              <p className="body-text-4 grey-70 ml-l mt-xxs">
                {t('web_and_app.allow_customers_explanation')}
              </p>
            )}
            <Radio value={SelectionType.NEAREST_LOCKED} className="mt-s">
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                {t('web_and_app.restrict_delivery')}
                <Badges values={[t('recommended')]} />
              </span>
            </Radio>
            {formData.deliverySelection === SelectionType.NEAREST_LOCKED && (
              <p className="body-text-4 grey-70 ml-l mt-xxs">
                {t('web_and_app.restrict_delivery_explanation')}
              </p>
            )}
          </FormsyRadioGroup>

          <div className="mt-s"></div>
        </Section>

        <PublishBanner
          buttonText={t('publish')}
          isLoading={isSubmitting}
          disabled={false}
        />
      </CmsPage>
      <ConfirmModal
        title={t('modal.publish_changes')}
        description={t('modal.you_are_publishing')}
        confirmText={t('modal.publish')}
        isOpen={isModalOpen}
        isLoading={isSubmitting}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => handlePublish()}
      />
    </Formsy>
  )
}

export default DeliveryDefaultLogic
